import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin } from "lucide-react";
import { lineParent, MaskLine } from "./motion";
import { WeatherBadge } from "./WeatherBadge";

const HERO_IMG =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/6ebajbre_Nyhavn.webp";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-5%", "4%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.05]);

  return (
    <section id="top" ref={ref} data-testid="hero" className="relative pt-4 md:pt-10">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end pt-6 md:pt-10 pb-8">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex flex-wrap items-center gap-2 mb-6"
            >
              <WeatherBadge />
            </motion.div>

            <motion.h1
              variants={lineParent}
              initial="hidden"
              animate="show"
              className="font-display font-900 tracking-tighter leading-[0.85] text-5xl sm:text-6xl lg:text-7xl"
            >
              <MaskLine>Copenhagen</MaskLine>
              <MaskLine>Everything you</MaskLine>
              <MaskLine>need <span className="font-editorial italic font-400">on one page</span></MaskLine>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="font-body text-base md:text-lg text-black/70 max-w-xl mt-7 leading-relaxed"
            >
              What to see, how to get around, where to stay, which bakery to queue at, and
              how to ride a rental bike without getting glared at + a free itinerary planner.
            </motion.p>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="relative border border-zinc-200 shadow-brut-lg overflow-hidden aspect-[16/10]"
            >
              <motion.img
                src={HERO_IMG}
                alt="Colourful Nyhavn townhouses reflected in the canal"
                loading="eager"
                fetchpriority="high"
                decoding="async"
                style={{ y, scale }}
                className="absolute inset-0 w-full h-[112%] object-cover object-[center_62%]"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
